import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import {
  listAllDistricts,
  listPoliceDivisions,
  listPoliceStations,
  type DistrictOption,
  type PoliceDivisionOption,
  type PoliceStationRow,
} from '../../services/policeStations';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { fltSelClass } from '../../utils/alSelClass';
import { consumeFlashToast } from '../../utils/flashToast';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';

export default function PoliceStationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PoliceStationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftDistrictId, setDraftDistrictId] = useState('');
  const [draftPoliceDivisionId, setDraftPoliceDivisionId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [policeDivisionId, setPoliceDivisionId] = useState('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [draftPoliceDivisions, setDraftPoliceDivisions] = useState<PoliceDivisionOption[]>([]);
  const [allPoliceDivisions, setAllPoliceDivisions] = useState<PoliceDivisionOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [divisionOptionsLoading, setDivisionOptionsLoading] = useState(false);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setOptionsLoading(true);
    void Promise.all([listAllDistricts(), listPoliceDivisions()]).then(([districtsRes, divisionsRes]) => {
      if (!alive) return;
      setDistricts(
        districtsRes.success
          ? [...districtsRes.data].sort((a, b) => a.name.localeCompare(b.name))
          : [],
      );
      setAllPoliceDivisions(
        divisionsRes.success
          ? [...divisionsRes.data].sort((a, b) => a.name.localeCompare(b.name))
          : [],
      );
      setOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!draftDistrictId) {
      setDraftPoliceDivisions([]);
      return;
    }
    let alive = true;
    setDivisionOptionsLoading(true);
    void listPoliceDivisions({ districtId: draftDistrictId }).then((res) => {
      if (!alive) return;
      setDraftPoliceDivisions(
        res.success ? [...res.data].sort((a, b) => a.name.localeCompare(b.name)) : [],
      );
      setDivisionOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [draftDistrictId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await listPoliceStations({
      districtId: districtId || undefined,
      policeDivisionId: policeDivisionId || undefined,
      page: uiPage - 1,
      pageSize,
    });
    if (!res.success) {
      setError(res.message || t('metadata.policeStations.loadFailed'));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    if (uiPage > res.data.totalPages) {
      setUiPage(res.data.totalPages);
      setLoading(false);
      return;
    }
    setRows(res.data.items);
    setTotal(res.data.total);
    setTotalPages(res.data.totalPages);
    setLoading(false);
  }, [districtId, policeDivisionId, reloadToken, uiPage, pageSize, t]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const policeDivisionFilterOptions = draftDistrictId ? draftPoliceDivisions : allPoliceDivisions;

  const districtNameByDivisionId = useMemo(
    () => new Map(allPoliceDivisions.map((d) => [d.id, d.districtName])),
    [allPoliceDivisions],
  );

  const columns = useMemo<Column<PoliceStationRow>[]>(() => {
    return [
      {
        key: 'name',
        header: t('metadata.policeStations.name'),
        getValue: (r) => r.name,
        cellClassName: 'dt-clamp',
        render: (r) => <span style={{ fontWeight: 600 }}>{r.name || EMPTY}</span>,
      },
      {
        key: 'code',
        header: t('metadata.policeStations.code'),
        getValue: (r) => r.code,
        cellClassName: 'dt-clamp',
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.code || EMPTY}</span>,
      },
      {
        key: 'district',
        header: t('metadata.policeStations.district'),
        getValue: (r) => districtNameByDivisionId.get(r.policeDivisionId) ?? '',
        render: (r) => districtNameByDivisionId.get(r.policeDivisionId) || EMPTY,
      },
      {
        key: 'policeDivisionName',
        header: t('metadata.policeStations.policeDivision'),
        getValue: (r) => r.policeDivisionName,
        render: (r) => r.policeDivisionName || EMPTY,
      },
      {
        key: 'active',
        header: t('common.status'),
        getValue: (r) => (r.active ? t('common.active') : t('common.inactive')),
        render: (r) => (
          <span className={`al-pill al-pill-${r.active ? 'file-d' : 'user-d'}`}>
            {r.active ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/police-stations/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.policeStations.updatePoliceStation')}
                aria-label={t('metadata.policeStations.updatePoliceStation')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t, districtNameByDivisionId]);

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.policeStations.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.policeStations.title')}</div>
        </div>
        <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
          <Link to="/metadata/police-stations/new" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('metadata.policeStations.addPoliceStation')}
          </Link>
        </PermissionGate>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid">
          <div className="flt-f">
            <div className="flt-l">{t('metadata.policeStations.district')}</div>
            <select
              className={fltSelClass(draftDistrictId)}
              value={draftDistrictId}
              disabled={optionsLoading}
              onChange={(e) => {
                setDraftDistrictId(e.target.value);
                setDraftPoliceDivisionId('');
              }}
            >
              {optionsLoading ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : districts.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('metadata.policeStations.selectDistrict')}
                  </option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="flt-f">
            <div className="flt-l">{t('metadata.policeStations.policeDivision')}</div>
            <select
              className={fltSelClass(draftPoliceDivisionId)}
              value={draftPoliceDivisionId}
              disabled={optionsLoading || (draftDistrictId ? divisionOptionsLoading : false)}
              onChange={(e) => setDraftPoliceDivisionId(e.target.value)}
            >
              {optionsLoading || (draftDistrictId && divisionOptionsLoading) ? (
                <option value="" disabled>
                  {t('common.loading')}
                </option>
              ) : policeDivisionFilterOptions.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('metadata.policeStations.selectPoliceDivision')}
                  </option>
                  {policeDivisionFilterOptions.map((div) => (
                    <option key={div.id} value={div.id}>
                      {div.name} ({div.districtName})
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setDraftDistrictId('');
              setDraftPoliceDivisionId('');
              setDistrictId('');
              setPoliceDivisionId('');
              setUiPage(1);
              setPageSize(10);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setDistrictId(draftDistrictId);
              setPoliceDivisionId(draftPoliceDivisionId);
              setUiPage(1);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.policeStations.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.policeStations.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: total,
          totalPages,
          page: uiPage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
